import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "./dropdown-menu";

// Enhanced Select interfaces for single/multiple mode
export interface SelectOption {
    value: string;
    label: string;
}

// Overloaded interfaces for better type safety
export interface SingleSelectProps {
    mode?: "single";
    options?: SelectOption[];
    value?: string;
    onValueChange?: (value: string) => void;
    placeholder?: string;
    className?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    maxDisplay?: number;
}

export interface MultiSelectProps {
    mode: "multiple";
    options: SelectOption[];
    value?: string[];
    onValueChange?: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    maxDisplay?: number;
}

export type SelectProps = SingleSelectProps | MultiSelectProps;

const SelectContext = React.createContext<{
    mode: "single" | "multiple";
    options?: SelectOption[];
    value?: any;
    onValueChange?: (value: any) => void;
    placeholder?: string;
    maxDisplay?: number;
    disabled?: boolean;
}>({
    mode: "single",
});

function Select({
    mode = "single",
    options,
    value,
    onValueChange,
    placeholder,
    className: _className,
    children,
    disabled,
    maxDisplay = 3,
    ...props
}: SelectProps & React.ComponentProps<typeof SelectPrimitive.Root>) {
    const contextValue = React.useMemo(
        () => ({
            mode,
            options,
            value,
            onValueChange,
            placeholder,
            maxDisplay,
            disabled,
        }),
        [mode, options, value, onValueChange, placeholder, maxDisplay, disabled]
    );

    if (mode === "multiple" && options) {
        return (
            <SelectContext.Provider value={contextValue}>
                <DropdownMenu>{children}</DropdownMenu>
            </SelectContext.Provider>
        );
    }

    // Default single select mode
    return (
        <SelectContext.Provider value={contextValue}>
            <SelectPrimitive.Root
                data-slot="select"
                value={typeof value === "string" ? value : undefined}
                onValueChange={onValueChange as any}
                disabled={disabled}
                {...props}
            >
                {children}
            </SelectPrimitive.Root>
        </SelectContext.Provider>
    );
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
    return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
    className,
    size = "default",
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
    size?: "sm" | "default";
}) {
    const context = React.useContext(SelectContext);

    // Move useMemo outside conditional to follow Rules of Hooks
    const displayText = React.useMemo(() => {
        if (context.mode !== "multiple") return "";

        const value = (context.value as string[]) || [];
        const options = context.options || [];

        if (value.length === 0) return context.placeholder || "Select options...";
        if (value.length <= (context.maxDisplay || 3)) {
            return value.map((val) => options.find((opt) => opt.value === val)?.label || val).join(", ");
        }
        return `${value.length} items selected`;
    }, [context.mode, context.value, context.options, context.placeholder, context.maxDisplay]);

    if (context.mode === "multiple") {
        return (
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size={size === "sm" ? "sm" : "default"}
                    disabled={context.disabled}
                    className={cn(
                        "setu:justify-between setu:min-w-[200px] setu:text-left setu:font-normal",
                        (!context.value || (context.value as string[]).length === 0) && "setu:text-muted-foreground",
                        className
                    )}
                    {...props}
                >
                    <span className="setu:truncate">{displayText}</span>
                    <ChevronDownIcon className="setu:ml-2 setu:h-4 setu:w-4 setu:shrink-0 setu:opacity-50" />
                </Button>
            </DropdownMenuTrigger>
        );
    }

    // Single select mode
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                "setu:!border-input setu:data-[placeholder]:text-muted-foreground setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:focus-visible:!border-ring setu:focus-visible:ring-ring/50 setu:aria-invalid:ring-destructive/20 dark:setu:aria-invalid:ring-destructive/40 setu:aria-invalid:!border-destructive dark:setu:bg-input/30 dark:setu:hover:bg-input/50 setu:flex setu:w-fit setu:items-center setu:justify-between setu:gap-2 setu:rounded-md setu:!border setu:bg-transparent setu:px-3 setu:py-2 setu:text-sm setu:whitespace-nowrap setu:shadow-xs setu:transition-[color,box-shadow] setu:outline-none setu:focus-visible:ring-[3px] setu:disabled:cursor-not-allowed setu:disabled:opacity-50 setu:data-[size=default]:h-9 setu:data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="setu:size-4 setu:opacity-50" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
}

function SelectContent({
    className,
    children,
    position = "popper",
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
    const context = React.useContext(SelectContext);

    if (context.mode === "multiple") {
        const handleSelectChange = (optionValue: string) => {
            if (context.disabled) return; // Prevent changes when disabled

            const currentValue = (context.value as string[]) || [];
            const newValue = currentValue.includes(optionValue)
                ? currentValue.filter((item) => item !== optionValue)
                : [...currentValue, optionValue];

            context.onValueChange?.(newValue);
        };

        const isOptionSelected = (optionValue: string): boolean => {
            return ((context.value as string[]) || []).includes(optionValue);
        };

        return (
            <DropdownMenuContent
                className={cn("setu:w-56", className)}
                onCloseAutoFocus={(e) => e.preventDefault()}
                {...props}
            >
                <DropdownMenuLabel>Select Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {context.options?.map((option) => (
                    <div
                        key={option.value}
                        className="setu:relative setu:flex setu:w-full setu:cursor-default setu:items-center setu:rounded-sm setu:py-1.5 setu:pr-8 setu:pl-2 setu:text-sm setu:outline-none setu:hover:bg-accent setu:hover:text-accent-foreground setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50"
                        onClick={() => handleSelectChange(option.value)}
                    >
                        <span className="setu:absolute setu:right-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                            {isOptionSelected(option.value) && <CheckIcon className="setu:size-4" />}
                        </span>
                        {option.label}
                    </div>
                ))}
                {children}
            </DropdownMenuContent>
        );
    }

    // Single select mode
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                className={cn(
                    "setu:bg-popover setu:text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 setu:relative setu:z-50 setu:max-h-(--radix-select-content-available-height) setu:min-w-[8rem] setu:origin-(--radix-select-content-transform-origin) setu:overflow-x-hidden setu:overflow-y-auto setu:rounded-md setu:!border setu:shadow-md",
                    position === "popper" &&
                        "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
                    className
                )}
                position={position}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    className={cn(
                        "setu:p-1",
                        position === "popper" &&
                            "setu:h-[var(--radix-select-trigger-height)] setu:w-full setu:min-w-[var(--radix-select-trigger-width)] setu:scroll-my-1"
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
    return (
        <SelectPrimitive.Label
            data-slot="select-label"
            className={cn("setu:text-muted-foreground setu:px-2 setu:py-1.5 setu:text-xs", className)}
            {...props}
        />
    );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                "setu:focus:bg-accent setu:focus:text-accent-foreground setu:[&_svg:not([class*='text-'])]:text-muted-foreground setu:relative setu:flex setu:w-full setu:cursor-default setu:items-center setu:gap-2 setu:rounded-sm setu:py-1.5 setu:pr-8 setu:pl-2 setu:text-sm setu:outline-hidden setu:select-none setu:data-[disabled]:pointer-events-none setu:data-[disabled]:opacity-50 setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
                className
            )}
            {...props}
        >
            <span className="setu:absolute setu:right-2 setu:flex setu:size-3.5 setu:items-center setu:justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="setu:size-4" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
    return (
        <SelectPrimitive.Separator
            data-slot="select-separator"
            className={cn("setu:!bg-border setu:pointer-events-none setu:-mx-1 setu:my-1 setu:h-px", className)}
            {...props}
        />
    );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn("setu:flex setu:cursor-default setu:items-center setu:justify-center setu:py-1", className)}
            {...props}
        >
            <ChevronUpIcon className="setu:size-4" />
        </SelectPrimitive.ScrollUpButton>
    );
}

function SelectScrollDownButton({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn("setu:flex setu:cursor-default setu:items-center setu:justify-center setu:py-1", className)}
            {...props}
        >
            <ChevronDownIcon className="setu:size-4" />
        </SelectPrimitive.ScrollDownButton>
    );
}

// Typed wrapper for MultiSelect to avoid union type issues
export function MultiSelect({
    options,
    value = [],
    onValueChange,
    placeholder = "Select options...",
    className,
    maxDisplay = 3,
    ...props
}: {
    options: SelectOption[];
    value?: string[];
    onValueChange?: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    maxDisplay?: number;
} & Omit<React.ComponentProps<typeof DropdownMenu>, "children">) {
    return (
        <Select
            mode="multiple"
            options={options}
            value={value as any}
            onValueChange={onValueChange as any}
            placeholder={placeholder}
            className={className}
            maxDisplay={maxDisplay}
            {...props}
        >
            <SelectTrigger className={className} />
            <SelectContent />
        </Select>
    );
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};
